/**
 * PublicMobileDrawer.tsx — Mobile off-canvas drawer for public visitors.
 *
 * Counterpart to <SidebarDrawer> for the public context. Renders the same
 * links as <PublicTopNav> (Beranda / Game / Worksheet) plus the "Masuk"
 * login entry as a full-width button, so visitors on mobile can navigate
 * the marketing surface without the horizontal nav being squashed off
 * screen.
 *
 * Includes the same accessibility patterns as <SidebarDrawer>:
 *   - Backdrop overlay (click to close)
 *   - ESC key to close
 *   - Focus trap (Tab cycles within drawer; Shift+Tab reverses)
 *   - Restore focus to the trigger button on close
 *   - prefers-reduced-motion respected (instant show/hide)
 *   - aria-modal + role="dialog" semantics
 *   - body scroll lock while open
 */

import type { JSX } from 'react';
import { useEffect, useRef } from 'react';
import { NavIcon } from './NavIcon.js';

export interface PublicMobileDrawerProps {
  pathname: string;
  open: boolean;
  onClose: () => void;
  /** ref to the hamburger trigger so we can restore focus on close */
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
}

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function PublicMobileDrawer({
  pathname,
  open,
  onClose,
  triggerRef,
}: PublicMobileDrawerProps): JSX.Element | null {
  const drawerRef = useRef<HTMLDivElement | null>(null);

  // Lock body scroll while the drawer is open.
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [open]);

  // Move focus into the drawer when it opens; restore on close.
  useEffect(() => {
    if (open && drawerRef.current) {
      const first = drawerRef.current.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      first?.focus();
    } else if (!open && triggerRef?.current) {
      triggerRef.current.focus();
    }
  }, [open, triggerRef]);

  // ESC + focus trap
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const drawer = drawerRef.current;
      if (!drawer) return;
      const focusables = Array.from(
        drawer.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => !el.hasAttribute('disabled'));
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const links = [
    { label: 'Beranda', href: '/', exact: true, icon: 'home' as const },
    { label: 'Game', href: '/games', icon: 'game' as const },
    { label: 'Worksheet', href: '/worksheets', icon: 'worksheet' as const },
  ];

  return (
    <div className="public-mobile-drawer-root" data-open={open ? 'true' : 'false'}>
      <button
        type="button"
        className="public-mobile-drawer-backdrop"
        aria-label="Tutup menu"
        onClick={onClose}
        tabIndex={-1}
      />
      <div
        ref={drawerRef}
        id="public-mobile-drawer"
        className="public-mobile-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Menu utama"
        data-testid="public-mobile-drawer"
      >
        <header className="public-mobile-drawer-header">
          <span className="public-mobile-drawer-title">Menu</span>
          <button
            type="button"
            className="public-mobile-drawer-close"
            aria-label="Tutup menu"
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <nav className="public-mobile-drawer-nav" aria-label="Menu utama">
          <ul className="public-mobile-drawer-list" role="list">
            {links.map((link) => {
              const active = link.exact ? pathname === link.href : pathname.startsWith(link.href);
              return (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className={`public-mobile-drawer-link ${active ? 'is-active' : ''}`}
                    aria-current={active ? 'page' : undefined}
                    onClick={onClose}
                    data-testid={`public-mobile-drawer-link-${link.label.toLowerCase()}`}
                  >
                    <span className="public-mobile-drawer-link-icon" aria-hidden="true">
                      <NavIcon icon={link.icon} size={20} />
                    </span>
                    <span className="public-mobile-drawer-link-label">{link.label}</span>
                  </a>
                </li>
              );
            })}
          </ul>
          <div className="public-mobile-drawer-cta">
            <a
              href="/login"
              className="button button-primary public-mobile-drawer-login"
              onClick={onClose}
              data-testid="public-mobile-drawer-login"
            >
              Masuk dengan kode undangan
            </a>
          </div>
        </nav>
      </div>
    </div>
  );
}
